import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface Option { value: string; label: string }

interface SelectProps {
  label?: string;
  placeholder?: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  includeDynamicOthers?: boolean; // when true, shows 'Others' if no search matches
  disabled?: boolean;
  error?: string;
  className?: string;
  native?: boolean; // when true, render a simple HTML select that scrolls naturally
  portal?: boolean; // when true (default), render dropdown in a portal (floating). If false, render inline below trigger
  showSearchButton?: boolean; // toggle the Search button next to the search input
  allowSearch?: boolean;
}

const Select: React.FC<SelectProps> = ({ 
  label, 
  placeholder = 'Select...', 
  options, 
  value, 
  onChange, 
  required, 
  includeDynamicOthers = false,
  disabled = false,
  error,
  className = '',
  native = false,
  portal = true,
  showSearchButton = true,
  allowSearch = true
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isAnimating, setIsAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<'below' | 'above'>('below');
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; position: 'below' | 'above' } | null>(null);
  const scrollParentsRef = useRef<(HTMLElement | Window)[] | null>(null);

  // Enhanced click outside handler
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      const target = e.target as Node;
      
      // Check if click is outside the select component
      if (!ref.current.contains(target)) {
        // Also check if click is not on the portal dropdown
        const dropdown = document.querySelector('[data-select-dropdown]');
        if (!dropdown || !dropdown.contains(target)) {
          handleClose();
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Decide whether to open above/below based on available viewport space
  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 320;
    const dropdownMinHeight = 200;
    const padding = 16;
    const spaceBelow = viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const shouldOpenAbove = spaceBelow < dropdownMinHeight && spaceAbove > spaceBelow;
    setPosition(shouldOpenAbove ? 'above' : 'below');
    let top: number;
    if (shouldOpenAbove) {
      top = rect.top - Math.min(dropdownMaxHeight, spaceAbove);
    } else {
      top = rect.bottom + 4;
    }
    let left = rect.left;
    const width = rect.width;
    if (left + width > viewportWidth - padding) {
      left = viewportWidth - width - padding;
    }
    if (left < padding) left = padding;
    setCoords({ top: Math.max(padding, top), left, width: Math.min(width, viewportWidth - 2 * padding), position: shouldOpenAbove ? 'above' : 'below' });
  }, []);

  const getScrollParents = useCallback((element: HTMLElement | null): (HTMLElement | Window)[] => {
    const result: (HTMLElement | Window)[] = [];
    if (!element) return [window];
    let parent: HTMLElement | null = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && parent.scrollHeight > parent.clientHeight;
      if (isScrollable) result.push(parent);
      parent = parent.parentElement;
    }
    result.push(window);
    return result;
  }, []);

  // Enhanced open/close handlers with animations
  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsAnimating(true);
    setOpen(true);
    // Focus search input after animation
    // setTimeout(() => {
    //   searchInputRef.current?.focus();
    // }, 100);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setIsAnimating(true);
    setOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
    // Reset animation state after close animation
    setTimeout(() => {
      setIsAnimating(false);
    }, 200);
  }, []);

  // Debounced position update to prevent excessive calculations
  const debouncedComputePosition = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(computePosition, 16); // ~60fps
    };
  }, [computePosition]);

  // Update position when dropdown opens and on scroll/resize
  useEffect(() => {
    if (open) {
      computePosition();
      const debouncedUpdate = debouncedComputePosition();
      const onResize = debouncedUpdate;
      const onScroll = debouncedUpdate;
      const parents = getScrollParents(triggerRef.current);
      scrollParentsRef.current = parents;
      window.addEventListener('resize', onResize);
      parents.forEach((p) => p.addEventListener('scroll', onScroll, { passive: true } as any));
      return () => {
        window.removeEventListener('resize', onResize);
        if (scrollParentsRef.current) {
          scrollParentsRef.current.forEach((p) => p.removeEventListener('scroll', onScroll as any));
        }
        scrollParentsRef.current = null;
      };
    }
  }, [open, computePosition, getScrollParents, debouncedComputePosition]);

  // Enhanced filtering with better search
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const searchTerm = query.toLowerCase().trim();
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm) ||
      option.value.toLowerCase().includes(searchTerm)
    );
  }, [options, query]);

  const finalOptions = useMemo(() => {
    if (filtered.length > 0) return filtered;
    if (includeDynamicOthers && query.trim()) {
      return [{ value: 'OTHERS', label: `Others (${query})` }];
    }
    return filtered;
  }, [filtered, includeDynamicOthers, query]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [finalOptions]);

  // Enhanced keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < finalOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : finalOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < finalOptions.length) {
          const selectedOption = finalOptions[highlightedIndex];
          onChange(selectedOption.value);
          handleClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
      case 'Tab':
        handleClose();
        break;
    }
  }, [open, finalOptions, highlightedIndex, onChange, handleOpen, handleClose]);

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  // Handle option selection
  const handleOptionSelect = useCallback((option: Option) => {
    onChange(option.value);
    handleClose();
  }, [onChange, handleClose]);

  // Native select rendering (default): behaves like a normal dropdown, no portals, no floating
  if (native) {
    return (
      <div className={`w-full ${className}`} ref={ref}>
        {label && (
          <label className="form-label">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={required}
          className={`w-full px-4 py-3 rounded-2xl border-2 bg-white transition-all duration-200 ${
            disabled
              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
              : error
              ? 'border-red-300 bg-red-50 focus:ring-4 focus:ring-red-100 focus:border-red-500'
              : 'border-gray-200 focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500'
          }`}
        >
          {/* Placeholder */}
          <option value="" disabled={required && !value}>
            {placeholder}
          </option>
          {options
            .filter((o) => o.value !== '')
            .map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </select>

        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full relative ${className}`} ref={ref}>
      {label && (
        <label className="block text-sm font-semibold text-gray-800 mb-3">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <button 
        ref={triggerRef} 
        type="button" 
        disabled={disabled}
        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 text-left group relative overflow-hidden ${
          disabled
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : open 
              ? 'border-green-500 bg-green-50 shadow-xl ring-4 ring-green-100' 
              : error
              ? 'border-red-300 bg-red-50 hover:border-red-400 focus:ring-4 focus:ring-red-100 focus:border-red-500'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen'
        }`} 
        onClick={open ? handleClose : handleOpen}
        onKeyDown={handleKeyDown}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        <div className="flex items-center justify-between relative z-10">
          <span className={`truncate ${selectedLabel ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center space-x-2">
            {value && (
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            )}
            <svg 
              className={`w-5 h-5 text-gray-400 transition-all duration-300 ${
                open 
                  ? 'rotate-180 text-green-500 scale-110' 
                  : 'group-hover:text-gray-600 group-hover:scale-110'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {/* Dropdown */}
      {open && portal && createPortal(
        <div 
          ref={dropdownRef}
          data-select-dropdown
          className={`select-dropdown fixed z-[10000] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm ${
            isAnimating 
              ? (coords?.position === 'above')
                ? 'animate-in slide-in-from-bottom-2 fade-in-0 zoom-in-95 duration-200'
                : 'animate-in slide-in-from-top-2 fade-in-0 zoom-in-95 duration-200'
              : ''
          }`}
          style={{ top: coords?.top, left: coords?.left, width: coords?.width, maxHeight: '320px', minHeight: '200px' }}
        >
          {/* Enhanced search bar with Search button */}
          { allowSearch &&
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search options..."
                  className="w-full pl-10 pr-8 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                  // autoFocus
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear search"
                  >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              {showSearchButton && (
                <button
                  type="button"
                  onClick={() => {
                    // Keep dropdown open and re-focus list using current query
                    dropdownRef.current?.focus?.();
                  }}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap"
                  aria-label="Search"
                >
                  Search
                </button>
              )}
            </div>
          </div>
          }

          {/* Enhanced options list */}
          <div className="max-h-64 overflow-y-auto dropdown-scrollbar">
            {finalOptions.map((opt, index) => (
              <button 
                key={opt.value} 
                type="button" 
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(opt);
                }} 
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`select-option w-full text-left px-4 py-4 transition-all duration-200 border-b border-gray-50 last:border-b-0 cursor-pointer group relative overflow-hidden ${
                  value === opt.value 
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border-l-4 border-green-500 font-semibold shadow-sm' 
                    : highlightedIndex === index
                    ? 'bg-gradient-to-r from-primary-lightGreen/10 to-primary-teaGreen/10 text-primary-lightGreen border-l-4 border-primary-lightGreen/30'
                    : 'text-gray-800 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:border-l-4 hover:border-gray-300'
                }`}
              >
                {/* Subtle hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                
                <div className="flex items-center justify-between relative z-10">
                  <span className="truncate font-medium">{opt.label}</span>
                  <div className="flex items-center space-x-2">
                    {value === opt.value && (
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    )}
                    {value === opt.value && (
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
            ))}
            
            {finalOptions.length === 0 && (
              <div className="px-4 py-8 text-center">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm text-gray-500 font-medium">No results found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
              </div>
            )}
          </div>

          {/* Footer with option count */}
          {finalOptions.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                {finalOptions.length} option{finalOptions.length !== 1 ? 's' : ''} available
              </p>
            </div>
          )}
        </div>,
        document.body
      )}

      {open && !portal && (
        <div 
          ref={dropdownRef}
          className={`absolute left-0 right-0 mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm ${
            isAnimating ? 'animate-in fade-in-0 zoom-in-95 duration-200' : ''
          }`}
          style={{ maxHeight: '320px', minHeight: '200px' }}
        >
          {/* Enhanced search bar with Search button */}
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search options..."
                  className="w-full pl-10 pr-8 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                  autoFocus
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear search"
                  >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              {showSearchButton && (
                <button
                  type="button"
                  onClick={() => {
                    dropdownRef.current?.focus?.();
                  }}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap"
                  aria-label="Search"
                >
                  Search
                </button>
              )}
            </div>
          </div>

          {/* Enhanced options list */}
          <div className="max-h-64 overflow-y-auto dropdown-scrollbar">
            {finalOptions.map((opt, index) => (
              <button 
                key={opt.value} 
                type="button" 
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(opt);
                }} 
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`select-option w-full text-left px-4 py-4 transition-all duration-200 border-b border-gray-50 last:border-b-0 cursor-pointer group relative overflow-hidden ${
                  value === opt.value 
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border-l-4 border-green-500 font-semibold shadow-sm' 
                    : highlightedIndex === index
                    ? 'bg-gradient-to-r from-primary-lightGreen/10 to-primary-teaGreen/10 text-primary-lightGreen border-l-4 border-primary-lightGreen/30'
                    : 'text-gray-800 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:border-l-4 hover:border-gray-300'
                }`}
              >
                {/* Subtle hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                
                <div className="flex items-center justify-between relative z-10">
                  <span className="truncate font-medium">{opt.label}</span>
                  <div className="flex items-center space-x-2">
                    {value === opt.value && (
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    )}
                    {value === opt.value && (
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {finalOptions.length === 0 && (
              <div className="px-4 py-8 text-center">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm text-gray-500 font-medium">No results found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
              </div>
            )}
          </div>

          {finalOptions.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                {finalOptions.length} option{finalOptions.length !== 1 ? 's' : ''} available
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
